import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Card, Row, Col, Form, Spinner, Badge } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AnnualLeaveManagement = () => {
  const [leaveDetails, setLeaveDetails] = useState([]); 
  const [allRequests, setAllRequests] = useState([]);   
  const [branches, setBranches] = useState([]); 
  const [users, setUsers] = useState([]);      
  const [loading, setLoading] = useState(false);

  const getInitialDates = () => {
    const now = new Date();
    return {
      start: `${now.getFullYear()}-01-01`,
      end: `${now.getFullYear()}-12-31`
    };
  };

  const initDates = getInitialDates();
  const [filters, setFilters] = useState({
    startDate: initDates.start,
    endDate: initDates.end,
    userId: "",    
    branchId: ""   
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchInitData = async () => {
    try {
      const branchUrl = API_ENDPOINTS.branches.startsWith("http") 
                        ? API_ENDPOINTS.branches 
                        : `${API_ENDPOINTS.url}/api${API_ENDPOINTS.branches}`;

      const [bRes, uRes] = await Promise.all([
        axiosClient.get(branchUrl, { headers: getAuthHeader() }),
        axiosClient.get(API_ENDPOINTS.users, { headers: getAuthHeader() })
      ]);
      setBranches(bRes.data || []);
      setUsers(uRes.data || []);
    } catch (e) {
      console.error("❌ Init metadata error:", e);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const urlRecords = API_ENDPOINTS.annualLeaveRecords;
    const urlRequests = `${API_ENDPOINTS.leaveRequests}/leave-requests`;

    try {
      const [detailsRes, requestsRes] = await Promise.all([
        axiosClient.get(urlRecords), 
        axiosClient.get(urlRequests) 
      ]);
      setLeaveDetails(detailsRes.data || []);
      setAllRequests(requestsRes.data || []);
    } catch (err) {
      console.error("❌ Load leave data error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitData();
    fetchData();
  }, [fetchData]);

  const processedData = useMemo(() => {
    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);

    return leaveDetails
      .filter(user => {
        const matchBranch = filters.branchId ? String(user.branchId || user.BranchId) === String(filters.branchId) : true;
        const matchUser = filters.userId ? String(user.userId || user.UserId) === String(filters.userId) : true;
        return matchBranch && matchUser;
      })
      .map(user => {
        const userRequestsInRange = allRequests.filter(req => {
          const reqDate = new Date(req.startDate || req.StartDate);
          const rUserId = req.userId || req.UserId;
          const rStatus = req.status || req.Status;
          const rType = req.type || req.Type;

          return (
            rUserId === user.userId &&
            rStatus === 11 && 
            (rType === 0 || rType === 5) && 
            reqDate >= start &&
            reqDate <= end
          );
        });

        const filteredUsedDays = userRequestsInRange.reduce((sum, req) => sum + (req.totalDays || req.TotalDays || 0), 0);
        const seniority = user.seniorityLeave || user.SeniorityLeave || 0;
        const accrued = user.monthlyAccruedLeave || user.MonthlyAccruedLeave || 0;
        const current = user.currentLeave || user.CurrentLeave || 0;
        const systemUsed = user.usedLeave || user.UsedLeave || 0;

        const totalFund = seniority + accrued + current;
        const actualRemaining = totalFund - systemUsed;

        return {
          ...user,
          fullName: user.fullName || user.FullName,
          joinedDate: user.joinedDate || user.JoinedDate,
          userId: user.userId || user.UserId,
          usedLeave: systemUsed,
          filteredUsedDays,
          totalFund,
          actualRemaining
        };
      });
  }, [leaveDetails, allRequests, filters]);

  const exportToExcel = () => {
    if (processedData.length === 0) return alert("No data to export!");
    const excelData = processedData.map(r => ({
      "UID": r.userId,
      "Full Name": r.fullName,
      "Joined Date": new Date(r.joinedDate).toLocaleDateString("en-GB"),
      "Total Fund": r.totalFund.toFixed(2),
      [`Used (${filters.startDate} to ${filters.endDate})`]: r.filteredUsedDays.toFixed(2),
      "Total Used (System)": r.usedLeave.toFixed(2),
      "Remaining": r.actualRemaining.toFixed(2)
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LeaveReport");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(data, `Leave_Balance_Report_${new Date().getTime()}.xlsx`);
  };

  return (
    <div className="p-4 bg-white min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-dark text-uppercase m-0">Annual Leave Management</h4>
          <p className="text-muted small mb-0">Track and manage employee leave balances</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={fetchData}>Refresh</Button>
          <Button variant="success" size="sm" className="fw-bold" onClick={exportToExcel}>
            EXPORT EXCEL
          </Button>
        </div>
      </div>

      <Card className="mb-3 border-0 bg-light shadow-sm p-3">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Form.Label className="small fw-bold">From Date</Form.Label>
            <Form.Control type="date" size="sm" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})}/>
          </Col>
          <Col md={2}>
            <Form.Label className="small fw-bold">To Date</Form.Label>
            <Form.Control type="date" size="sm" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})}/>
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-bold">Branch / Department</Form.Label>
            <Form.Select size="sm" value={filters.branchId} onChange={e => setFilters({ ...filters, branchId: e.target.value })}>
              <option value="">-- All Branches --</option>
              {branches.map(b => <option key={b.id || b.Id} value={b.id || b.Id}>{b.branchName || b.BranchName}</option>)}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-bold">Employee</Form.Label>
            <Form.Select size="sm" value={filters.userId} onChange={e => setFilters({ ...filters, userId: e.target.value })}>
              <option value="">-- All Employees --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.userName} - {u.fullName}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button variant="dark" className="w-100 fw-bold" size="sm" onClick={fetchData}>SEARCH</Button>
          </Col>
        </Row>
      </Card>

      {/* --- PHẦN GHI CHÚ CÔNG THỨC --- */}
      <div className="mb-4 px-2">
        <div className="d-flex gap-4 text-muted small italic">
          <span><strong>Formula 1:</strong> $$Total Fund = Seniority + Accrued + Current$$</span>
          <span><strong>Formula 2:</strong> $$Remaining = Total Fund - Total Used$$</span>
        </div>
      </div>

      <div className="table-responsive shadow-sm rounded">
        <Table bordered hover className="mb-0 bg-white">
          <thead className="text-center">
            <tr style={{ backgroundColor: "#4a69bd", color: "white" }}>
              <th>UID</th>
              <th>EMPLOYEE NAME</th>
              <th>JOINED DATE</th>
              <th>FUND (S+M+C)</th>
              <th className="bg-warning text-dark">USED (PERIOD)</th>
              <th>TOTAL USED</th>
              <th className="bg-success text-white">REMAINING</th>
            </tr>
          </thead>
          <tbody className="text-center align-middle">
            {loading ? (
              <tr><td colSpan="7"><Spinner animation="border" size="sm" /> Loading...</td></tr>
            ) : processedData.length > 0 ? (
              processedData.map((r, i) => (
                <tr key={i}>
                  <td>{r.userId}</td>
                  <td className="text-start fw-bold">{r.fullName}</td>
                  <td>{r.joinedDate ? new Date(r.joinedDate).toLocaleDateString("en-GB") : "N/A"}</td>
                  <td><Badge bg="secondary">{r.totalFund.toFixed(1)}</Badge></td>
                  <td className="fw-bold text-danger">{r.filteredUsedDays.toFixed(1)}</td>
                  <td>{r.usedLeave.toFixed(1)}</td>
                  <td className="fw-bold text-success" style={{ fontSize: "1.1rem" }}>
                    {r.actualRemaining.toFixed(1)}
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7">No records found matching filters.</td></tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default AnnualLeaveManagement;